import {SlButton, SlCarousel} from '@shoelace-style/shoelace/shoelace';
import {notNull} from '../../../../../../library/clxProductNet/assets/js/modules/common/assert';
import {MicroConversionTrackingEventsEnum} from '../../common/trackingMicroconversion/Model/EventProductCustomizationEnum';
import ProductCustomizationConfigModel from '../Model/productCustomizationConfigModel';
import CustomizationPricingOverviewComponent from './customizationPricingOverviewComponent';
import ProductCustomizationComponent from './productCustomizationComponent';

export default class MountingPositionComponent extends HTMLElement {
    private refreshOverviewUI: CustomizationPricingOverviewComponent;
    private glider: HTMLSpanElement | null = null;
    private customizationComponent: ProductCustomizationComponent | null = null;

    private readonly config: ProductCustomizationConfigModel;

    constructor() {
        super();
        this.refreshOverviewUI = notNull(document.querySelector<CustomizationPricingOverviewComponent>('#pricing-overview'));
        this.config = ProductCustomizationConfigModel.getInstance();
    }

    connectedCallback() {
        this.glider = notNull(this.querySelector<HTMLSpanElement>('.glider'));
        this.customizationComponent = notNull(document.querySelector<ProductCustomizationComponent>('clx-customization-product-customization'));
        this.attachMountingPositionEventListeners();
    }

    disconnectedCallback() {
        this._detachMountingPositionEventListeners();
        this.glider = null;
        this.customizationComponent = null;
    }

    private handleItemClick = (item: HTMLDivElement | SlButton) => async (e: MouseEvent) => {
        const clickedElement = notNull(e.target) as SlButton;

        if (clickedElement.classList.contains('delete-mounting-position')) {
            this.deactivateMountingPosition(item);
        } else {
            this.activateAndShowSelectedMountingPosition(item);
        }
        await this.updateCustomizationOverview(MicroConversionTrackingEventsEnum.customization_configuration_change);
    };

    private attachMountingPositionEventListeners() {
        const positions = this.querySelectorAll<HTMLDivElement>('.item[data-posid]');

        for (const item of positions) {
            item.addEventListener('click', this.handleItemClick(item));

            const posid = notNull(item.getAttribute('data-posid'));
            const addMountingPositionButton = notNull(this.querySelector<SlButton>(`.add-mounting-position[data-posid="${posid}"]`));
            addMountingPositionButton.addEventListener('click', this.handleItemClick(item));
        }
    }

    private _detachMountingPositionEventListeners() {
        const positions = this.querySelectorAll<HTMLDivElement>('.item[data-posid]');
        for (const item of positions) {
            item.removeEventListener('click', this.handleItemClick(item));

            const posid = notNull(item.getAttribute('data-posid'));
            const addMountingPositionButton = notNull(this.querySelector<SlButton>(`.add-mounting-position[data-posid="${posid}"]`));
            addMountingPositionButton.addEventListener('click', this.handleItemClick(item));
        }
    }

    private async updateCustomizationOverview(eventName: string): Promise<void> {
        await this.refreshOverviewUI.refreshOverviewUIAndHandleErrors(this.config, eventName);
    }

    private activateAndShowSelectedMountingPosition(item: HTMLDivElement | SlButton) {
        const positionId = parseInt(item.dataset.posid || '0', 10);
        this.config.activateMountingPosition(positionId);

        let currentPosition = this.querySelector<HTMLDivElement>('.positions .item.active');
        if (currentPosition) {
            currentPosition.classList.remove('active');
        }
        item.classList.add('active', 'is-configured');

        const currentContentContainer = this.querySelector<HTMLDivElement>('.position-decoration.active');
        if (currentContentContainer) {
            currentContentContainer.classList.remove('active');
        }
        const contentContainer = notNull(this.querySelector<HTMLDivElement>(`.position-decoration.position-${positionId}`));
        contentContainer.classList.add('active');

        notNull(this.customizationComponent).activateGotoCheckoutButton();

        this.updateSummaryTextInMountingPositionSliderItem();
        this.moveGliderToPosition(item);
        this.goToActiveSlide(item);
    }

    private goToActiveSlide(item: HTMLDivElement | SlButton) {
        const carousel = notNull(this.querySelector('sl-carousel'));
        const carouselItems = notNull(this.querySelectorAll('sl-carousel-item'));

        carouselItems.forEach((carouselItem, index) => {
            const innerItemOfCarouselItem = notNull(carouselItem.querySelector('.item'));
            if (innerItemOfCarouselItem.getAttribute('data-posid') === item.getAttribute('data-posid')) {
                // current carousel item is active item
                carousel.goToSlide(index);
            }
        });
    }

    private deactivateMountingPosition(item: HTMLDivElement | SlButton) {
        const positionId = parseInt(item.dataset.posid || '0', 10);
        this.config.deactivateMountingPosition(positionId);
        item.classList.remove('active', 'is-configured');

        const posIdOfActiveElement = notNull(item.getAttribute('data-posid'));
        const activeButtonToAddMountingPosition = notNull(this.querySelector(`.add-mounting-position[data-posid="${posIdOfActiveElement}"]`));
        activeButtonToAddMountingPosition.classList.remove('active', 'is-configured');

        const contentContainer = notNull(this.querySelector<HTMLDivElement>(`.position-decoration.position-${positionId}`));
        contentContainer.classList.remove('active');

        this.moveGliderToNextConfiguredPositionIfPossible();
    }

    private moveGliderToPosition(configuredMountingPositionItem: HTMLElement) {
        const glider = notNull(this.glider);

        const itemSlideNumber = parseInt(configuredMountingPositionItem.getAttribute('data-slide-number') || '0');
        const slideGap = this.getSlideGapFromSliderDOM();

        // this is a translate with x * 100% + x * 10px (100% for each item and 10px for the gap between the items)
        const slideOffset = itemSlideNumber * 100;
        const gap = slideGap! * itemSlideNumber;
        glider.style.display = 'block';
        glider.style.transform = `translateX(calc(${slideOffset}% + ${gap}px)`;
    }

    private hideGlider() {
        const glider = notNull(this.glider);
        glider.style.display = 'none';
    }

    private moveGliderToNextConfiguredPositionIfPossible() {
        const configuredMountingPositionItem = this.querySelector<HTMLDivElement>('.positions .item.is-configured');
        const configuredMountingPositionButton = this.querySelector<HTMLDivElement>('.positions .add-mounting-position.is-configured');

        const customizationComponent = notNull(this.customizationComponent);
        if (configuredMountingPositionItem) {
            customizationComponent.activateGotoCheckoutButton();
            this.activateAndShowSelectedMountingPosition(configuredMountingPositionItem);
            this.moveGliderToPosition(configuredMountingPositionItem);
        } else if (configuredMountingPositionButton) {
            customizationComponent.activateGotoCheckoutButton();
            this.activateAndShowSelectedMountingPosition(configuredMountingPositionButton);
            const posidOfButton = notNull(configuredMountingPositionButton.getAttribute('data-posid'));
            const activeItem = notNull(this.querySelector<HTMLDivElement>(`.item[data-posid="${posidOfButton}"]`));
            this.moveGliderToPosition(activeItem);
        } else {
            customizationComponent.deactivateGotoCheckoutButton();
            this.hideGlider();
        }
    }

    private updateSummaryTextInMountingPositionSliderItem() {
        const activeItem = notNull(this.querySelector<HTMLDivElement>('.positions .active'));
        const posIdOfActiveElement = notNull(activeItem.getAttribute('data-posid'));
        const finishingColorText = notNull(
            this.querySelector<HTMLDivElement>(`.positions .item[data-posid="${posIdOfActiveElement}"] .display-finishing-color`),
        );
        const finishingProcessText = notNull(
            this.querySelector<HTMLDivElement>(`.positions .item[data-posid="${posIdOfActiveElement}"] .display-finishing-process`),
        );

        const activeFinishingColor: HTMLDivElement = notNull(
            this.querySelector<HTMLDivElement>('.position-decoration.active .motif-colors .item.active .number-of-colors-text'),
        );
        const activeFinishingProcess: HTMLDivElement = notNull(
            this.querySelector<HTMLDivElement>('.position-decoration.active .processes .item.active .name-of-process'),
        );

        finishingColorText.innerText = activeFinishingColor.innerText.trim();
        finishingProcessText.innerText = activeFinishingProcess.innerText.trim();
    }

    public deactivateMountingPositionById(posId: Number) {
        const item = notNull(this.querySelector<HTMLDivElement>(`.item[data-posid="${posId}"]`));
        this.deactivateMountingPosition(item);
    }

    private getSlideGapFromSliderDOM() {
        const slider = notNull(this.querySelector<SlCarousel>('sl-carousel'));
        const css = getComputedStyle(slider);

        return parseInt(css.getPropertyValue('--slide-gap'), 10);
    }
}
customElements.define('clx-customization-mounting-position', MountingPositionComponent);
